// Iter91 — Offline retry queue for post-SOS location refinements.
//
// `useLocationRefinement` fires PATCH /alerts/{id}/location on every
// strictly-better GPS fix. If the device is offline (or the request
// otherwise fails), the attempt used to be silently dropped — the
// alert kept whatever fix it had at creation time forever, even once
// connectivity returned seconds later. This module persists the
// SINGLE best pending fix per alert (localStorage survives a dropped
// network / a backgrounded tab) and retries it once the browser comes
// back online, on next app boot, or on a periodic backstop timer.
//
// Never creates a new alert — `flush()` only ever calls PATCH on an
// alert id that already exists. A fix past its own refinement-window
// deadline is dropped (the backend would 410 it anyway).
const KEY = "rr_location_refinement_queue";

const _read = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_e) { return {}; }
};

const _write = (q) => {
  try { localStorage.setItem(KEY, JSON.stringify(q)); }
  catch (_e) { /* storage unavailable (private mode, quota) — best effort only */ }
};

/**
 * Queue (or replace) the best-known pending fix for one alert. Keeps
 * only ONE entry per alert id — if a better fix is already queued,
 * the weaker one is ignored.
 */
export function enqueueLocationRefinement(alertId, coords, deadline) {
  if (!alertId) return;
  const q = _read();
  const existing = q[alertId];
  if (
    existing &&
    existing.coords?.accuracy != null &&
    coords?.accuracy != null &&
    existing.coords.accuracy <= coords.accuracy
  ) {
    return; // already queued fix is as good or better — nothing to do
  }
  q[alertId] = { coords, deadline };
  _write(q);
}

/**
 * Attempt to flush every pending entry via `sendFn(alertId, coords)`.
 * Entries past their deadline are dropped without being sent. Entries
 * that still fail (offline, server rejects) stay queued for the next
 * flush. Safe to call opportunistically and often — a no-op when the
 * queue is empty.
 */
export async function flushLocationRefinementQueue(sendFn) {
  const q = _read();
  const ids = Object.keys(q);
  if (ids.length === 0) return;
  const now = Date.now();
  let changed = false;
  for (const alertId of ids) {
    const entry = q[alertId];
    if (!entry || now > entry.deadline) {
      delete q[alertId];
      changed = true;
      continue;
    }
    try {
      await sendFn(alertId, entry.coords);
      delete q[alertId];
      changed = true;
    } catch (_e) {
      // Still offline / still failing — leave queued for next attempt.
    }
  }
  if (changed) _write(q);
}
